import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { StringUtils } from '@bingads-webui-clientcenter/common-utils';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { TaxTypeFormPropTypes } from '../shapes';
import { GlobalTaxForm } from './global-taxform';
import { EmptyTaxForm } from './empty-taxform';
import { registerTaxform } from '../taxform-store';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { EGTaxViewTaxForm } from './viewtaxforms/egypt-viewtaxform';
import { CustomValidateItems } from '../validator';
import { withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getPilotStatus, getHelpPopupTopicFromTaxSchema, getTaxTypeLabel } from '../utils';
import { GlobalTaxIdHelpPopUpId, GlobalTaxIdCountryCodeToPilots, requiredFeatures, InputTaxFieldId } from '../constants';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  TaxEGVatNumberRegExp, TaxUINRegExp, TaxType, TaxLabelFormat, CountryCode,
} = Constants;
const { regexTest } = StringUtils;
const taxIdValidator = _.partial(regexTest, TaxEGVatNumberRegExp);
const UINValidator = _.partial(regexTest, TaxUINRegExp);

const taxSchemaGetter = (props) => {
  const { permissions } = props;
  const IsABLEgyptTaxUpdatePilotOn = getPilotStatus(requiredFeatures.ABLEgyptTaxUpdate, permissions);
  const taxSchema = {
    name: 'Egypt Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.VatNumber]: {
        type: 'string',
        required: false,
        helpPopupTopic: GlobalTaxIdHelpPopUpId,
      },
    },
  };
  if (IsABLEgyptTaxUpdatePilotOn) {
    taxSchema.properties[TaxType.UIN] = {
      type: 'string',
      helpPopupTopic: 'pop_BA_Account_TaxVATEgyptUIN',
    };
  }
  return taxSchema;
};

const EGTaxForm = ({
  i18n,
  onValidate,
  showLineByLineFormGroup,
  alwaysShowError,
  data,
  mtData,
  taxCertification,
  mtTaxCertification,
  urlBuilder,
  disabled,
  permissions,
}) => {
  const validationI18n = useValidationI18n(i18n);
  const taxSchema = taxSchemaGetter({ permissions });
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const IsABLEgyptTaxUpdatePilotOn = getPilotStatus(requiredFeatures.ABLEgyptTaxUpdate, permissions);
  const vatValidatorErrorMessage = useMemo(() => i18n.getString(CustomValidateItems.EgyptVATFormatValidation), [i18n]);
  const TRNValidatorErrorMessage = useMemo(() => i18n.getString(CustomValidateItems.EgyptTRNFormatValidation), [i18n]);
  const UINValidatorErrorMessage = useMemo(() => i18n.getString(CustomValidateItems.UINValidation), [i18n]);
  const vatLabel = useMemo(() => getTaxTypeLabel(TaxType.VatNumber, i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.EG }, permissions), [i18n, permissions]);
  const UINLabel = useMemo(() => getTaxTypeLabel(TaxType.UIN, i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.EG }, permissions), [i18n, permissions]);
  const [taxDataState, setTaxDataState] = useState({ data, isValid: false });
  const hasVatNumber = !_.isEmpty(taxDataState.data.VatNumber);
  const hasUIN = !_.isEmpty(taxDataState.data.UIN);

  const getVatPassiveMessages = useCallback(() => {
    if (hasVatNumber) {
      if (taxIdValidator(taxDataState.data.VatNumber)) {
        return [];
      }
      return [TRNValidatorErrorMessage];
    }
    if (hasUIN) {
      return [i18n.getString(_TL_('This field is required'))];
    }
    return [];
  }, [hasVatNumber, hasUIN, taxDataState.data.VatNumber, TRNValidatorErrorMessage, i18n]);

  const getUINPassiveMessages = useCallback(() => {
    if (hasUIN) {
      if (UINValidator(taxDataState.data.UIN)) {
        return [];
      }
      return [UINValidatorErrorMessage];
    }
    if (hasVatNumber) {
      return [i18n.getString(_TL_('This field is required'))];
    }
    return [];
  }, [hasUIN, hasVatNumber, taxDataState.data.UIN, UINValidatorErrorMessage, i18n]);

  const onTaxFormValidate = useCallback(({ data: taxData, isValid }) => (
    setTaxDataState({
      data: taxData,
      isValid,
    })
  ), []);

  useEffect(() => {
    if (IsABLEgyptTaxUpdatePilotOn) {
      const isVatTaxDataValid = getVatPassiveMessages().length === 0;
      const isUINValid = getUINPassiveMessages().length === 0;
      const isValid = taxDataState.isValid && isVatTaxDataValid && isUINValid;
      onValidate({
        data: taxDataState.data,
        taxCertification: {
          taxCertifications: [],
          reverseChargeDocuments: [],
        },
        isValid,
      });
    }
  }, [taxDataState, getVatPassiveMessages, getUINPassiveMessages, IsABLEgyptTaxUpdatePilotOn, onValidate]);

  // Clear all data when unmount
  // useLayoutEffect to make sure it runs before the next component's effect
  useLayoutEffect(() => {
    if (IsABLEgyptTaxUpdatePilotOn) {
      return () => {
        onValidate({
          data: {},
          taxCertification: null,
          isValid: true,
        });
      };
    }
    return () => {};
  }, [IsABLEgyptTaxUpdatePilotOn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {IsABLEgyptTaxUpdatePilotOn ?
        <ValidationGroup
          validationProps={{
            schema: taxSchema,
            i18n: validationI18n,
            onValidate: onTaxFormValidate,
            data,
          }}
        >
          <CustomizedFormGroup
            isStatic
            label={vatLabel}
            helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.VatNumber })}
          >
            <Input
              id={InputTaxFieldId(TaxType.VatNumber)}
              validationProps={{
                field: TaxType.VatNumber,
                alwaysShowError,
                showErrorOnPassiveMessages: true,
                passiveMessages: getVatPassiveMessages(),
              }}
              disabled={disabled}
            />
          </CustomizedFormGroup>
          <CustomizedFormGroup
            isStatic
            label={UINLabel}
            helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema, taxType: TaxType.UIN })}
          >
            <Input
              id={InputTaxFieldId(TaxType.UIN)}
              validationProps={{
                  field: TaxType.UIN,
                  alwaysShowError,
                  showErrorOnPassiveMessages: true,
                  passiveMessages: getUINPassiveMessages(),
                }}
              disabled={disabled}
            />
          </CustomizedFormGroup>
        </ValidationGroup>
        :
        <GlobalTaxForm
          i18n={i18n}
          onValidate={onValidate}
          taxSchemaGetter={taxSchemaGetter}
          vatLabel={vatLabel}
          showLineByLineFormGroup={showLineByLineFormGroup}
          vatValidatorErrorMessage={vatValidatorErrorMessage}
          alwaysShowError={alwaysShowError}
          data={data}
          mtData={mtData}
          taxCertification={taxCertification}
          mtTaxCertification={mtTaxCertification}
          urlBuilder={urlBuilder}
          disabled={disabled}
          taxIdValidator={taxIdValidator}
          showTaxCert={false}
        />
      }
    </>
  );
};

EGTaxForm.propTypes = TaxTypeFormPropTypes;

const pilotFlag = GlobalTaxIdCountryCodeToPilots.EG;
const TaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: EGTaxForm, DefaultForm: EmptyTaxForm });
const ViewTaxForm = withTaxFormByPilot({ pilotFlag, PilotForm: EGTaxViewTaxForm, DefaultForm: EmptyViewTaxForm });

registerTaxform('/EG/', TaxForm, withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm, getTaxSchema: taxSchemaGetter }));
